import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Users, Briefcase, Banknote, TrendingUp, Building2, Factory, ShoppingBag, Award, Wallet, MapPin, Target, Percent } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../AuthContext';
import useAutoRefresh from '../hooks/useAutoRefresh';

const Dashboard = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [selectedBranch]);

  // Real-time auto-refresh: polls every 30s + on tab focus (silent — no full-screen spinner)
  useAutoRefresh(() => fetchStats(true), { intervalMs: 180000 });

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      setBranches(response.data);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchStats = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const params = {};
      if (selectedBranch && selectedBranch !== 'all') {
        params.branch_id = selectedBranch;
      }
      const response = await api.get('/dashboard/stats', { params });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total BRC-MC Members',
      value: stats?.total_brc_mc || 0,
      icon: Users,
      color: 'emerald',
    },
    {
      title: 'CRP-EPs Engaged / Active',
      value: stats?.total_crp_eps_engaged || 0,
      icon: Users,
      color: 'blue',
      subtitle: `${stats?.total_crp_eps_active || 0} Active Working`,
    },
    {
      title: 'Approved BP (CEF/CIF/Bank)',
      value: stats?.total_approved_bp || 0,
      icon: Award,
      color: 'purple',
    },
    {
      title: 'Promoted Enterprises (Grounded)',
      value: stats?.total_promoted_enterprises || 0,
      icon: Briefcase,
      color: 'orange',
    },
    {
      title: 'Manufacturing',
      value: stats?.total_manufacturing || 0,
      icon: Factory,
      color: 'slate',
    },
    {
      title: 'Trading',
      value: stats?.total_trading || 0,
      icon: ShoppingBag,
      color: 'amber',
    },
    {
      title: 'Service',
      value: stats?.total_service || 0,
      icon: Building2,
      color: 'cyan',
    },
    {
      title: 'Group Enterprises',
      value: stats?.total_group_enterprises || 0,
      icon: Users,
      color: 'teal',
    },
    {
      title: 'Existing Enterprises',
      value: stats?.total_existing_enterprises || 0,
      icon: Briefcase,
      color: 'indigo',
    },
    {
      title: 'New Enterprises',
      value: stats?.total_new_enterprises || 0,
      icon: Briefcase,
      color: 'green',
    },
    {
      title: 'Women Led Enterprises',
      value: stats?.total_women_led || 0,
      icon: TrendingUp,
      color: 'pink',
    },
    {
      title: 'Sub-Sector Based Enterprises',
      value: stats?.total_sub_sector_enterprises || 0,
      icon: Briefcase,
      color: 'lime',
    },
    {
      title: 'Convergent Enterprises',
      value: stats?.total_convergent_enterprises || 0,
      icon: Briefcase,
      color: 'sky',
    },
    {
      title: 'Shut Down Enterprises',
      value: stats?.total_shut_down || 0,
      icon: Briefcase,
      color: 'gray',
    },
    {
      title: 'SC/ST/OBC Entrepreneurs',
      value: stats?.total_sc_st_obc || 0,
      icon: Users,
      color: 'violet',
    },
    {
      title: 'CEF Loan Outstanding',
      value: `₹${(stats?.total_cef_outstanding || 0).toLocaleString('en-IN')}`,
      icon: Banknote,
      color: 'red',
      isAmount: true,
    },
    {
      title: 'Cash at Bank',
      value: `₹${(stats?.cash_at_bank || 0).toLocaleString('en-IN')}`,
      icon: Building2,
      color: 'emerald',
      isAmount: true,
    },
    {
      title: 'Cash in Hand',
      value: `₹${(stats?.cash_in_hand || 0).toLocaleString('en-IN')}`,
      icon: Wallet,
      color: 'amber',
      isAmount: true,
    },
  ];

  const getColorClasses = (color) => {
    const colors = {
      emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      blue: 'bg-blue-50 text-blue-700 border-blue-200',
      purple: 'bg-purple-50 text-purple-700 border-purple-200',
      orange: 'bg-orange-50 text-orange-700 border-orange-200',
      slate: 'bg-slate-50 text-slate-700 border-slate-200',
      amber: 'bg-amber-50 text-amber-700 border-amber-200',
      cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
      pink: 'bg-pink-50 text-pink-700 border-pink-200',
      indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      green: 'bg-green-50 text-green-700 border-green-200',
      violet: 'bg-violet-50 text-violet-700 border-violet-200',
      red: 'bg-red-50 text-red-700 border-red-200',
      teal: 'bg-teal-50 text-teal-700 border-teal-200',
      lime: 'bg-lime-50 text-lime-700 border-lime-200',
      sky: 'bg-sky-50 text-sky-700 border-sky-200',
      gray: 'bg-gray-50 text-gray-700 border-gray-200',
    };
    return colors[color] || colors.emerald;
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="dashboard">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
            {stats?.branch_name && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                <MapPin className="w-3 h-3" />
                {stats.branch_name}
              </span>
            )}
          </div>
          {/* Branch Filter for Admin */}
          {isAdmin() && (
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger className="w-48 h-8 text-sm" data-testid="dashboard-branch-filter">
                <SelectValue placeholder="Select Branch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <p className="text-sm text-slate-600">Overview of WWAGS BRC operations and key metrics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          const colorClasses = getColorClasses(stat.color);

          return (
            <div
              key={stat.label || index}
              data-testid={`stat-card-${index}`}
              onClick={() => navigate('/reports?report=statistics')}
              className="bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 p-3 relative overflow-hidden group cursor-pointer hover:border-emerald-300"
            >
              {/* Background decoration */}
              <div className={`absolute top-0 right-0 w-16 h-16 ${colorClasses.split(' ')[0]} opacity-5 rounded-full -mr-8 -mt-8`}></div>

              <div className="relative z-10">
                <div className="flex items-start justify-between mb-2">
                  <div className={`p-1.5 rounded-md ${colorClasses}`}>
                    <Icon className="w-4 h-4" strokeWidth={2} />
                  </div>
                </div>

                <div>
                  <p className="text-[10px] font-medium text-slate-500 mb-0.5 leading-tight">{stat.title}</p>
                  <p className={`text-lg font-bold text-slate-900 ${stat.isAmount ? 'text-base' : ''}`}>
                    {stat.value}
                  </p>
                  {stat.subtitle && (
                    <p className="text-[10px] text-slate-500 mt-0.5">{stat.subtitle}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Repayment/Collection Percentage Section */}
      {stats?.repayment_data && (
        <div className="mt-6">
          <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Percent className="w-4 h-4 text-emerald-700" />
            Repayment / Collection Status
          </h3>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3">
              <p className="text-xs text-slate-500 mb-1">Total Loans</p>
              <p className="text-lg font-bold text-slate-900">{stats.repayment_data.summary.total_loans}</p>
              <p className="text-[10px] text-slate-400">{stats.repayment_data.summary.active_loans} Active / {stats.repayment_data.summary.closed_loans} Closed</p>
            </div>
            <div className="bg-purple-50 rounded-lg border border-purple-200 shadow-sm p-3">
              <p className="text-xs text-purple-600 mb-1">Total Disbursed</p>
              <p className="text-lg font-bold text-purple-900">₹{(stats.repayment_data.summary.total_disbursed || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-blue-50 rounded-lg border border-blue-200 shadow-sm p-3">
              <p className="text-xs text-blue-600 mb-1">Expected Collection</p>
              <p className="text-lg font-bold text-blue-900">₹{(stats.repayment_data.summary.total_expected || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-emerald-50 rounded-lg border border-emerald-200 shadow-sm p-3">
              <p className="text-xs text-emerald-600 mb-1">Actual Collected</p>
              <p className="text-lg font-bold text-emerald-900">₹{(stats.repayment_data.summary.total_collected || 0).toLocaleString('en-IN')}</p>
            </div>
          </div>

          {/* Portfolio Repayment Percentage */}
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg shadow-sm p-4 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-100">Portfolio Repayment Percentage</p>
                <p className="text-3xl font-bold text-white">{stats.repayment_data.summary.portfolio_repayment_percentage}%</p>
              </div>
              <div className="w-20 h-20 rounded-full border-4 border-emerald-300 flex items-center justify-center bg-white/10">
                <span className="text-2xl font-bold text-white">{stats.repayment_data.summary.portfolio_repayment_percentage}%</span>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-3">
              <div className="h-2 bg-emerald-300/30 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(stats.repayment_data.summary.portfolio_repayment_percentage, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Branch-wise Repayment Table */}
          {stats.repayment_data.branch_wise && stats.repayment_data.branch_wise.length > 0 && (
            <div className="overflow-x-auto bg-white rounded-lg border border-slate-200 shadow-sm">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Branch</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-slate-600">Loans</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Disbursed</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Expected</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Collected</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-slate-600">Repayment %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {stats.repayment_data.branch_wise.map((branch, idx) => (
                    <tr key={branch.branch_id || branch.branch_name || idx} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-sm font-medium text-slate-900">{branch.branch_name}</td>
                      <td className="px-3 py-2 text-sm text-center text-slate-600">{branch.total_loans}</td>
                      <td className="px-3 py-2 text-sm text-right text-slate-700">₹{branch.total_disbursed.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-sm text-right text-blue-700">₹{branch.total_expected.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-sm text-right text-emerald-700">₹{branch.total_collected.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          branch.repayment_percentage >= 80 ? 'bg-emerald-100 text-emerald-800' :
                          branch.repayment_percentage >= 50 ? 'bg-amber-100 text-amber-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {branch.repayment_percentage}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* SVEP Target vs Achieved Section */}
      {stats?.target_vs_achieved && stats.target_vs_achieved.length > 0 && (
        <div className="mt-6">
          <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Target className="w-4 h-4 text-emerald-700" />
            SVEP Block Target vs Achieved
          </h3>
          <div className="overflow-x-auto bg-white rounded-lg border border-slate-200 shadow-sm">
            <table className="w-full">
              <thead className="bg-emerald-700 text-white">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold">Branch / Block</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold" colSpan="2">Entrepreneurs</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold" colSpan="2">CEF Loans</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold" colSpan="2">CIF Loans</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold" colSpan="2">Bank Loans</th>
                </tr>
                <tr className="bg-emerald-600">
                  <th className="px-3 py-1 text-left text-[10px] font-medium"></th>
                  <th className="px-2 py-1 text-center text-[10px] font-medium">Target</th>
                  <th className="px-2 py-1 text-center text-[10px] font-medium">Achieved</th>
                  <th className="px-2 py-1 text-center text-[10px] font-medium">Target</th>
                  <th className="px-2 py-1 text-center text-[10px] font-medium">Achieved</th>
                  <th className="px-2 py-1 text-center text-[10px] font-medium">Target</th>
                  <th className="px-2 py-1 text-center text-[10px] font-medium">Achieved</th>
                  <th className="px-2 py-1 text-center text-[10px] font-medium">Target</th>
                  <th className="px-2 py-1 text-center text-[10px] font-medium">Achieved</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stats.target_vs_achieved.map((branch, idx) => (
                  <tr key={branch.branch_id || branch.branch_name || idx} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-sm font-medium text-slate-900">{branch.branch_name}</td>
                    <td className="px-2 py-2 text-sm text-center text-slate-600">{branch.entrepreneurs.target}</td>
                    <td className="px-2 py-2 text-sm text-center">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                        branch.entrepreneurs.percentage >= 80 ? 'bg-emerald-100 text-emerald-800' :
                        branch.entrepreneurs.percentage >= 50 ? 'bg-amber-100 text-amber-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {branch.entrepreneurs.achieved} ({branch.entrepreneurs.percentage}%)
                      </span>
                    </td>
                    <td className="px-2 py-2 text-sm text-center text-slate-600">{branch.cef_loans.target_count}</td>
                    <td className="px-2 py-2 text-sm text-center">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                        branch.cef_loans.percentage_count >= 80 ? 'bg-emerald-100 text-emerald-800' :
                        branch.cef_loans.percentage_count >= 50 ? 'bg-amber-100 text-amber-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {branch.cef_loans.achieved_count} ({branch.cef_loans.percentage_count}%)
                      </span>
                    </td>
                    <td className="px-2 py-2 text-sm text-center text-slate-600">{branch.cif_loans.target_count}</td>
                    <td className="px-2 py-2 text-sm text-center">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                        branch.cif_loans.percentage_count >= 80 ? 'bg-emerald-100 text-emerald-800' :
                        branch.cif_loans.percentage_count >= 50 ? 'bg-amber-100 text-amber-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {branch.cif_loans.achieved_count} ({branch.cif_loans.percentage_count}%)
                      </span>
                    </td>
                    <td className="px-2 py-2 text-sm text-center text-slate-600">{branch.bank_loans.target_count}</td>
                    <td className="px-2 py-2 text-sm text-center">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                        branch.bank_loans.percentage_count >= 80 ? 'bg-emerald-100 text-emerald-800' :
                        branch.bank_loans.percentage_count >= 50 ? 'bg-amber-100 text-amber-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {branch.bank_loans.achieved_count} ({branch.bank_loans.percentage_count}%)
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
